import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "../Button";
import { Loader } from "../Loader";
import {
  downloadOptInTransactionsCsv,
  getOptInTransactions,
  OPT_IN_TRANSACTION_STATUS_LABELS,
  OptInTransaction,
  OptInTransactionStatusFilter,
  OptInTransactionsPage,
} from "../../api/optInOffers";
import { formatInteger } from "../../helpers/formatNumber";
import {
  dateToCopenhagenEndOfDayUTC,
  dateToCopenhagenStartOfDayUTC,
} from "../../helpers/dateFilters";
import { OptInTransactionsTable } from "./OptInTransactionsTable";
import { copenhagenToday, slug } from "./exportFileName";

const PAGE_SIZE = 50;

const STATUS_VALUES: OptInTransactionStatusFilter[] = [
  "All",
  "Paid",
  "Compensated",
  "NotOptedIn",
  "OtherFailure",
  "ManualCredit",
];

const EMPTY_COUNTS: OptInTransactionsPage["counts"] = {
  all: 0,
  paid: 0,
  compensated: 0,
  notOptedIn: 0,
  otherFailure: 0,
  manualCredit: 0,
};

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

interface OptInTransactionsTabProps {
  businessId: string;
  partnerName: string;
}

export const OptInTransactionsTab: React.FC<OptInTransactionsTabProps> = ({
  businessId,
  partnerName,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // URL is the source of truth; all params are prefixed `t` to stay apart from Customers.
  const statusParam = searchParams.get("tstatus") as OptInTransactionStatusFilter;
  const status = STATUS_VALUES.includes(statusParam) ? statusParam : "All";
  const appliedSearch = searchParams.get("tsearch") ?? "";
  const fromRaw = searchParams.get("tfrom") ?? "";
  const toRaw = searchParams.get("tto") ?? "";
  const fromDate = DATE_RE.test(fromRaw) ? fromRaw : "";
  const toDate = DATE_RE.test(toRaw) ? toRaw : "";
  const pageRaw = parseInt(searchParams.get("tpage") ?? "1", 10);
  const pageNumber = Number.isFinite(pageRaw) && pageRaw >= 1 ? pageRaw : 1;

  const [search, setSearch] = useState(appliedSearch);
  const [rows, setRows] = useState<OptInTransaction[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState(EMPTY_COUNTS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  useEffect(() => {
    setSearch(appliedSearch);
  }, [appliedSearch]);

  const update = (changes: Record<string, string | null>) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([key, value]) => {
      if (value) params.set(key, value);
      else params.delete(key);
    });
    setSearchParams(params);
  };

  const fromIso = fromDate ? dateToCopenhagenStartOfDayUTC(fromDate) : undefined;
  const toIso = toDate ? dateToCopenhagenEndOfDayUTC(toDate) : undefined;

  useEffect(() => {
    let ignore = false;
    const run = async () => {
      try {
        setLoading(true);
        const response = await getOptInTransactions(businessId, {
          status,
          search: appliedSearch || undefined,
          from: fromIso,
          to: toIso,
          pageNumber,
          pageSize: PAGE_SIZE,
        });
        if (ignore) return;
        setRows(response.data);
        setTotal(response.total);
        setCounts(response.counts);
        setError(null);
      } catch (err) {
        if (ignore) return;
        setError("Failed to fetch transactions");
        console.error("Error fetching opt-in transactions:", err);
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    run();
    return () => {
      ignore = true;
    };
  }, [businessId, status, appliedSearch, fromIso, toIso, pageNumber]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const chips: { value: OptInTransactionStatusFilter; label: string; count: number }[] = [
    { value: "All", label: "All", count: counts.all },
    { value: "Paid", label: OPT_IN_TRANSACTION_STATUS_LABELS.Paid, count: counts.paid },
    { value: "Compensated", label: OPT_IN_TRANSACTION_STATUS_LABELS.Compensated, count: counts.compensated },
    { value: "NotOptedIn", label: OPT_IN_TRANSACTION_STATUS_LABELS.NotOptedIn, count: counts.notOptedIn },
    { value: "OtherFailure", label: OPT_IN_TRANSACTION_STATUS_LABELS.OtherFailure, count: counts.otherFailure },
    { value: "ManualCredit", label: OPT_IN_TRANSACTION_STATUS_LABELS.ManualCredit, count: counts.manualCredit },
  ];

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    const fileName = `opt-in-transactions-${slug(partnerName)}-${copenhagenToday()}.csv`;
    try {
      await downloadOptInTransactionsCsv(
        businessId,
        { status, search: appliedSearch || undefined, from: fromIso, to: toIso },
        fileName
      );
    } catch (err) {
      // Blob responses hide the backend's JSON error body, so show a generic message.
      setExportError("Export failed. Please try again.");
      console.error("Error exporting opt-in transactions:", err);
    } finally {
      setExporting(false);
    }
  };

  const filtered = status !== "All" || appliedSearch || fromDate || toDate;

  return (
    <div data-testid="opt-in-transactions-tab" className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-2">
        {chips.map((chip) => (
          <button
            key={chip.value}
            type="button"
            onClick={() =>
              update({ tstatus: chip.value === "All" ? null : chip.value, tpage: null })
            }
            className={`px-3 py-1 rounded-full text-sm border ${
              status === chip.value
                ? "bg-purple-500 text-white border-purple-500"
                : "bg-white text-gray-700 border-gray-300"
            }`}
          >
            {chip.label} ({formatInteger(chip.count)})
          </button>
        ))}
      </div>

      <form
        className="flex flex-wrap items-end gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          update({ tsearch: search.trim() || null, tpage: null });
        }}
      >
        <div className="w-[360px]">
          <label
            htmlFor="opt-in-transactions-search"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Search (name, email)
          </label>
          <input
            id="opt-in-transactions-search"
            type="text"
            className="w-full p-2 border border-gray-300 rounded-md"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="e.g. a name or an email"
          />
        </div>
        <div>
          <label
            htmlFor="opt-in-transactions-from"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            From
          </label>
          <input
            id="opt-in-transactions-from"
            type="date"
            className="p-2 border border-gray-300 rounded-md"
            value={fromDate}
            onChange={(e) => update({ tfrom: e.target.value || null, tpage: null })}
          />
        </div>
        <div>
          <label
            htmlFor="opt-in-transactions-to"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            To
          </label>
          <input
            id="opt-in-transactions-to"
            type="date"
            className="p-2 border border-gray-300 rounded-md"
            value={toDate}
            onChange={(e) => update({ tto: e.target.value || null, tpage: null })}
          />
        </div>
        <div className="flex items-center gap-3">
          <Button type="submit">Apply</Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setSearch("");
              update({ tsearch: null, tfrom: null, tto: null, tpage: null });
            }}
          >
            Reset
          </Button>
          <Button type="button" variant="outline" onClick={handleExport} disabled={exporting}>
            Export CSV
          </Button>
        </div>
      </form>
      {exportError && <div className="error">{exportError}</div>}
      {error && <div className="error">{error}</div>}
      {loading && (
        <div className="flex justify-center my-4">
          <Loader />
        </div>
      )}

      <OptInTransactionsTable
        rows={rows}
        showPerson
        onPersonClick={(identityId) => update({ person: identityId })}
        emptyMessage={
          loading
            ? "Loading…"
            : filtered
            ? "No transactions match this filter."
            : "No transactions yet."
        }
      />

      <div className="flex justify-between items-center">
        <Button
          variant="outline"
          onClick={() => update({ tpage: pageNumber - 1 > 1 ? String(pageNumber - 1) : null })}
          disabled={pageNumber === 1}
        >
          Previous
        </Button>
        <span>
          Page {pageNumber} of {totalPages} · {formatInteger(total)} transactions
        </span>
        <Button
          variant="outline"
          onClick={() => update({ tpage: String(pageNumber + 1) })}
          disabled={pageNumber >= totalPages}
        >
          Next
        </Button>
      </div>
    </div>
  );
};
